import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, IconButton, Button, CircularProgress, Typography } from "@mui/material";
import CustomPagination from "../../components/CustomPagination";
import { 
    useGetNotificationsQuery, 
    useMarkAllAsReadMutation, 
    useMarkAsReadMutation, 
    useDeleteNotificationMutation,
    AppNotification
} from "../../service/notifications";
import { toast } from "react-toastify";
import moment from "moment";
import useAuth from "../../hooks/useAuth";

const RecievedNotifications = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const limit = 10;

    const user = useAuth();
    const { data, isLoading, isFetching } = useGetNotificationsQuery(
        {
            page: page + 1,
            limit,
            search: searchTerm,
        },
        { skip: !user }
    );

    const [markAllAsRead, { isLoading: isMarkingAll }] = useMarkAllAsReadMutation();
    const [markAsRead] = useMarkAsReadMutation();
    const [deleteNotification] = useDeleteNotificationMutation();

    const notificationsList = data?.data?.list || [];
    const total = data?.data?.total || 0;

    const handleMarkAllAsRead = async () => {
        try {
            await markAllAsRead().unwrap();
            toast.success("All notifications marked as read");
        } catch (error) {
            toast.error("Failed to mark all as read");
        }
    };

    const handleMarkAsRead = async (id: string, isRead: boolean) => {
        if (!isRead) {
            try {
                await markAsRead({ id }).unwrap();
            } catch (error) {
                console.error("Failed to mark as read", error);
            }
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await deleteNotification({ id }).unwrap();
            toast.success("Notification deleted");
        } catch (error) {
            toast.error("Failed to delete notification");
        }
    };

    return (
        <>
            <div className="main_title">
                <h1>Recieved Notifications</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
                    Recieved Notifications
                </p>
            </div>

            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <SearchBar
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={(val) => {
                                setSearchTerm(val);
                                setPage(0); // Reset page on search
                            }}
                            placeholder="Search..."
                        />
                    </div>
                    <div className="right_s">
                        <Button 
                            className="btn btn_primary" 
                            onClick={handleMarkAllAsRead} 
                            disabled={isMarkingAll || notificationsList.length === 0}
                        >
                            Mark All as Read
                        </Button>
                    </div>
                </div>
                
                <TableContainer className="table_container">
                    <Box className="heading"></Box>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table" className="v3">
                        <TableHead>
                            <TableRow hover>
                                <TableCell>User Name</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading || isFetching ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" style={{ padding: "40px" }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : notificationsList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" style={{ padding: "40px" }}>
                                        <Typography variant="body1" color="textSecondary">No notifications found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                notificationsList.map((notification: AppNotification) => (
                                    <TableRow 
                                        key={notification.id || notification._id} 
                                        hover 
                                        onClick={() => {
                                            const idToMark = notification.id || notification._id;
                                            if (idToMark) handleMarkAsRead(idToMark, notification.isRead);
                                        }}
                                        style={{ 
                                            cursor: "pointer", 
                                            backgroundColor: notification.isRead ? "transparent" : "#f0f8ff" 
                                        }}
                                    >
                                        <TableCell>
                                            <div className="user_block">
                                                <figure>
                                                    <img src={notification.sender?.profileImage || "/static/images/user_placeholder.png"} alt="" />
                                                </figure>
                                                <p>
                                                    <b style={{ fontWeight: notification.isRead ? "normal" : "bold" }}>
                                                        {notification.sender?.name || "System"}
                                                    </b>
                                                    <span>{notification.sender?.email || ""}</span>
                                                </p>
                                            </div>
                                        </TableCell>
                                        <TableCell style={{ fontWeight: notification.isRead ? "normal" : "bold" }}>
                                            {notification.broadcast?.title || notification.title}
                                        </TableCell>
                                        <TableCell className="max_c2">
                                            <p style={{ fontWeight: notification.isRead ? "normal" : "bold" }}>
                                                {notification.broadcast?.message || notification.message}
                                            </p>
                                        </TableCell>
                                        <TableCell style={{ fontWeight: notification.isRead ? "normal" : "bold" }}>
                                            {moment(notification.createdAt).format("DD MMM YYYY")}
                                        </TableCell>
                                        <TableCell>
                                            <div className="table_actions v2">
                                                <Box>
                                                    <IconButton 
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // prevent row click
                                                            const idToDelete = notification.id || notification._id;
                                                            if (idToDelete) handleDelete(idToDelete);
                                                        }}
                                                    >
                                                        <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                                    </IconButton>
                                                </Box>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!isLoading && !isFetching && total > 0 && (
                    <CustomPagination
                        count={total}
                        pageSize={limit}
                        currentPage={page}
                        onPageChange={(newPage) => setPage(newPage)}
                    />
                )}
            </div>
        </>
    );
};

export default RecievedNotifications;
